import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Moon, Sun, LayoutDashboard, Users, Shield, ChevronDown, UserCog, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { sectionForPath } from '../lib/sectionTheme';
import SectionNav from './SectionNav';

const LOGO_URL = "/brand/sp-logo.png";          // Sanctuary Point (light mode)
const LOGO_MARK = "/brand/sp-mark.png";         // SP mark (small icon spots)
const LOGO_DARK = "/brand/sp-logo.png";         // dark mode fallback

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const menuRef = useRef(null);
  const section = sectionForPath(location.pathname);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const isAdmin = user?.role === 'admin';
  const initials = user?.name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'U';

  const roleMeta = {
    admin: { label: 'Super Admin', icon: Shield, color: '#1f6f3b' },
    superuser: { label: 'Practice Manager', icon: UserCog, color: '#7C3AED' },
    radiologist: { label: 'Radiologist', icon: User, color: '#F59E0B' },
    reception: { label: 'Reception', icon: User, color: '#64748B' },
    radiographer: { label: 'Radiographer', icon: User, color: '#0EA5E9' },
    sonographer: { label: 'Sonographer', icon: User, color: '#14B8A6' },
    nursing: { label: 'Nursing', icon: User, color: '#EC4899' },
    trainee: { label: 'Trainee', icon: User, color: '#8B5CF6' },
    staff: { label: 'Staff', icon: User, color: '#64748B' },
  };
  const R = roleMeta[user?.role] || roleMeta.reception;

  return (
    <>
      <header className="sticky top-0 z-50 bg-background/85 backdrop-blur-xl border-b border-border i3-page-accent-top" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
              <img src={dark ? LOGO_DARK : LOGO_URL}
                alt="SP Radiology Assistant"
                className="h-9 sm:h-10 w-auto object-contain"
                onError={(e) => { e.target.src = LOGO_MARK; }} />
              <span className="hidden sm:inline text-[10px] font-semibold tracking-[0.14em] uppercase" style={{ color: section.color }}>Assistant</span>
            </Link>

            {/* Active section indicator (desktop) */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ borderColor: section.ring, backgroundColor: section.colorBg }} data-testid="section-indicator">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: section.color }} />
              <span className="text-xs font-semibold tracking-wide" style={{ color: section.color }}>{section.label}</span>
            </div>

            <div className="flex items-center gap-1">
              <button data-testid="theme-toggle" onClick={toggle} className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent transition-colors" title={dark ? 'Light mode' : 'Dark mode'}>
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {user ? (
                <div className="relative" ref={menuRef}>
                  <button data-testid="user-menu-btn" onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-full hover:bg-accent transition-colors border border-transparent hover:border-border">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ backgroundColor: R.color }}>{initials}</div>
                    <span className="hidden sm:block text-xs font-semibold text-foreground">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </button>

                  {menuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-xl shadow-xl overflow-hidden" data-testid="user-menu">
                      <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-semibold text-foreground truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${R.color}1A`, color: R.color }}>
                          <R.icon className="w-3 h-3" />{R.label}
                        </span>
                      </div>
                      <div className="py-1">
                        <Link to="/profile" onClick={() => setMenuOpen(false)} data-testid="menu-profile" className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors">
                          <User className="w-4 h-4 text-muted-foreground" /> My Profile
                        </Link>
                        {isPM && (
                          <>
                            <Link to="/dashboard" onClick={() => setMenuOpen(false)} data-testid="menu-dashboard" className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors">
                              <LayoutDashboard className="w-4 h-4 text-muted-foreground" /> Management Dashboard
                            </Link>
                            <Link to="/people" onClick={() => setMenuOpen(false)} data-testid="menu-people" className="flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors">
                              <Users className="w-4 h-4 text-muted-foreground" /> People &amp; licensing
                            </Link>
                            <Link to="/management" onClick={() => setMenuOpen(false)} data-testid="menu-users" className="flex items-center gap-2.5 px-4 py-2 text-sm text-muted-foreground hover:bg-accent transition-colors">
                              <Users className="w-4 h-4 text-muted-foreground" /> App settings (legacy)
                            </Link>
                            <div className="h-px bg-border my-1" />
                          </>
                        )}
                        <button onClick={handleLogout} data-testid="menu-logout" className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors">
                          <LogOut className="w-4 h-4" /> Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-sm font-medium rounded-lg transition-colors" style={{ color: section.color }}>Sign In</Link>
              )}

              <button data-testid="mobile-menu-btn" className="md:hidden p-2 text-muted-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && user && (
          <div className="md:hidden border-t border-border bg-card" data-testid="mobile-menu">
            <nav className="px-4 py-3 space-y-1">
              {isPM && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent rounded-lg">Management Dashboard</Link>}
              {isPM && <Link to="/people" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent rounded-lg">People &amp; licensing</Link>}
              {isPM && <Link to="/management" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent rounded-lg">App settings (legacy)</Link>}
              <button onClick={handleLogout} className="block w-full text-left px-3 py-2.5 text-sm font-medium text-red-500 hover:bg-red-500/10 rounded-lg">Sign out</button>
            </nav>
          </div>
        )}
      </header>
      {user && <SectionNav />}
    </>
  );
}
